import { useEffect } from 'react';

import {
  installConnectionBrowserListeners,
  useConnectionStore,
  type ConnectionStatus,
} from '../../stores/connectionStore';

const STATUS_LABELS: Record<Exclude<ConnectionStatus, 'connected'>, string> = {
  reconnecting: 'Reconnecting to the server...',
  offline: 'You are offline. Changes will retry when the connection returns.',
};

const STATUS_CLASSES: Record<Exclude<ConnectionStatus, 'connected'>, string> = {
  reconnecting: 'bg-amber-600/90 text-amber-50 border-amber-400',
  offline: 'bg-rose-700/95 text-rose-50 border-rose-500',
};

/**
 * Slim banner shown across the top of the app shell whenever the connection
 * store is in a degraded state. Also installs the browser online/offline
 * listeners when mounted so the store stays in sync with device state.
 */
export function ConnectionBanner(): JSX.Element | null {
  const status = useConnectionStore((state) => state.status);

  useEffect(() => {
    return installConnectionBrowserListeners();
  }, []);

  if (status === 'connected') {
    return null;
  }

  const label = STATUS_LABELS[status];
  const className = STATUS_CLASSES[status];

  return (
    <div
      aria-live="polite"
      className={`pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-4 py-2`}
      role="status"
    >
      <div
        className={`pointer-events-auto rounded-b-md border border-t-0 px-4 py-1.5 text-xs font-medium shadow-lg ${className}`}
      >
        {label}
      </div>
    </div>
  );
}
